/**
 * Help & Support Page
 * Hero search, Quick actions, FAQ, Contact, Resources
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Search, DollarSign, Headphones, BookOpen, ChevronDown, ChevronUp,
  Mail, Phone, FileText, Video, Bug, MessageCircle, ArrowLeft, HelpCircle
} from 'lucide-react';

const FAQ_ITEMS = [
  {
    q: 'How are my residual payouts calculated?',
    a: 'Your residual payouts are calculated based on the net profit from your merchants multiplied by your assigned split percentage. For example, if a merchant generates $100 net profit and your split is 50%, you\'ll receive $50.',
  },
  {
    q: 'When are residuals paid out?',
    a: 'Residuals are typically processed monthly after the processor statements are received and reconciled. You can request payouts through the system, and your agent will be notified to approve the payment.',
  },
  {
    q: 'What if I don\'t see a merchant in my residuals?',
    a: 'If a merchant is missing from your residuals, it could be due to: inactive status, zero profit for that month, or the merchant being assigned to a different agent. Contact support for assistance.',
  },
  {
    q: 'How do I request a payout?',
    a: 'Click the "Request Payout" button next to any unpaid residual entry. This will send a notification to your supervising agent who can approve and process the payment.',
  },
];

const QUICK_ACTIONS = [
  { icon: DollarSign, title: 'Residuals Help', desc: 'Learn about payouts, splits, and commission calculations', color: 'text-green-600 bg-green-50' },
  { icon: Headphones, title: 'Contact Support', desc: 'Get direct help from our support team', color: 'text-blue-600 bg-blue-50' },
  { icon: BookOpen, title: 'User Guide', desc: 'Complete documentation and tutorials', color: 'text-purple-600 bg-purple-50' },
];

const RESOURCES = [
  { icon: FileText, title: 'User Manual', desc: 'Complete guide to using the residuals system', link: 'Download PDF', color: 'text-red-600' },
  { icon: Video, title: 'Video Tutorials', desc: 'Step-by-step video guides', link: 'Watch Videos', color: 'text-purple-600' },
  { icon: Bug, title: 'Report Issue', desc: 'Found a bug or have feedback?', link: 'Submit Report', color: 'text-orange-600' },
  { icon: MessageCircle, title: 'Community Forum', desc: 'Connect with other users', link: 'Join Forum', color: 'text-teal-600' },
];

export default function HelpSupportPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  const filteredFaq = search.trim()
    ? FAQ_ITEMS.filter(f => f.q.toLowerCase().includes(search.toLowerCase()) || f.a.toLowerCase().includes(search.toLowerCase()))
    : FAQ_ITEMS;

  return (
    <div className="min-h-full" data-testid="help-support-page">
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-muted transition-colors" data-testid="help-back-btn">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-xl font-bold">Help & Support</h1>
      </div>

      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-600 to-blue-800 text-white px-4 sm:px-6 py-12 sm:py-16">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <HelpCircle className="h-7 w-7" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold mb-2">How can we help you?</h2>
          <p className="text-blue-100 text-sm mb-6">Find answers to common questions or get in touch with our support team</p>
          <div className="relative max-w-lg mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search for help articles..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-12 h-12 bg-white text-foreground rounded-xl border-0 shadow-lg"
              data-testid="help-search-input"
            />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-8 pb-12 space-y-8">
        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="quick-actions">
          {QUICK_ACTIONS.map((a, i) => {
            const Icon = a.icon;
            return (
              <div key={i} className="bg-card border rounded-xl p-5 hover:shadow-md transition-all cursor-pointer group" data-testid={`quick-action-${i}`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${a.color}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="text-sm font-semibold group-hover:text-primary transition-colors">{a.title}</h3>
                <p className="text-xs text-muted-foreground mt-1">{a.desc}</p>
              </div>
            );
          })}
        </div>

        {/* FAQ */}
        <div className="bg-card border rounded-xl" data-testid="faq-section">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold">Frequently Asked Questions</h3>
          </div>
          <div className="divide-y">
            {filteredFaq.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-sm text-muted-foreground">No matching questions found. Try a different search term.</p>
              </div>
            ) : (
              filteredFaq.map((faq, i) => (
                <div key={i}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-muted/30 transition-colors"
                    data-testid={`faq-item-${i}`}
                  >
                    <span className="text-sm font-medium pr-4">{faq.q}</span>
                    {openFaq === i ? <ChevronUp className="h-4 w-4 text-muted-foreground flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-5 -mt-1">
                      <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Contact */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="contact-section">
          <div className="bg-card border rounded-xl p-6">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mb-3">
              <Mail className="h-5 w-5 text-blue-600" />
            </div>
            <h3 className="text-sm font-semibold">Email Support</h3>
            <p className="text-xs text-muted-foreground mt-1 mb-3">Send us an email and we'll get back to you within 24 hours</p>
            <a href="mailto:support@company.com" className="text-sm font-medium text-primary hover:underline" data-testid="contact-email">
              support@company.com
            </a>
          </div>
          <div className="bg-card border rounded-xl p-6">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center mb-3">
              <Phone className="h-5 w-5 text-green-600" />
            </div>
            <h3 className="text-sm font-semibold">Phone Support</h3>
            <p className="text-xs text-muted-foreground mt-1 mb-3">Call us during business hours for immediate assistance</p>
            <a href="tel:+12345678900" className="text-sm font-medium text-primary hover:underline" data-testid="contact-phone">
              +1 (234) 567-8900
            </a>
            <p className="text-[10px] text-muted-foreground mt-1">Mon-Fri, 9AM-6PM EST</p>
          </div>
        </div>

        {/* Additional Resources */}
        <div className="bg-card border rounded-xl" data-testid="resources-section">
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold">Additional Resources</h3>
          </div>
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {RESOURCES.map((r, i) => {
              const Icon = r.icon;
              return (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/20 transition-colors cursor-pointer" data-testid={`resource-${i}`}>
                  <div className="w-9 h-9 bg-muted rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className={`h-4.5 w-4.5 ${r.color}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{r.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{r.desc}</p>
                    <span className="text-xs font-medium text-primary mt-1.5 inline-block hover:underline">{r.link}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer CTA */}
        <div className="text-center py-6">
          <p className="text-sm text-muted-foreground mb-3">Still need help? Our support team is here to assist you.</p>
          <Button size="lg" data-testid="footer-contact-btn">
            <Headphones className="h-4 w-4 mr-2" /> Contact Support
          </Button>
        </div>
      </div>
    </div>
  );
}
